import config from '@config'
import { Image, Link } from '@utils/cook/types'

export type EntityType = 'competitor' | 'competition' | 'default'

export interface SportEntity {
  entityId: string
  entityType: EntityType
  title: string
  link: Link
  isFollowingEnabled?: boolean
  sportradarId?: string
  abbreviation?: string
  image: Image<'free'>
  backgroundImage: Image<'16_9'>
}

export type SportEntitiesData = SportEntity[]

export type FetchSportEntities = (options: {
  isServerRequest?: boolean
}) => Promise<FormattedSportEntitiesData | undefined>

export interface FormattedSportEntitiesData {
  entitiesRegistry: Record<string, SportEntity>
  entitiesOrderedList: string[]
  followableEntitiesOrderedList: string[]
  entitiesByTypeOrderedList: Partial<Record<EntityType, string[]>>
  followableEntitiesByTypeOrderedList: Partial<Record<EntityType, string[]>>
  entityToSportradarIdMap: Record<string, string>
  sportradarIdToEntityMap: Record<string, string>
}

const {
  entities: { sportEntitiesBaseUrl },
} = config

const formatSportEntities = (
  sportEntities: SportEntitiesData
): FormattedSportEntitiesData => {
  return sportEntities.reduce(
    (acc, entity) => {
      acc.entitiesRegistry[entity.entityId] = entity
      acc.entitiesOrderedList.push(entity.entityId)

      if (!Array.isArray(acc.entitiesByTypeOrderedList[entity.entityType])) {
        acc.entitiesByTypeOrderedList[entity.entityType] = []
      }

      ;(acc.entitiesByTypeOrderedList[entity.entityType] as string[]).push(
        entity.entityId
      )

      if (entity.isFollowingEnabled) {
        acc.followableEntitiesOrderedList.push(entity.entityId)

        if (
          !Array.isArray(
            acc.followableEntitiesByTypeOrderedList[entity.entityType]
          )
        ) {
          acc.followableEntitiesByTypeOrderedList[entity.entityType] = []
        }

        ;(
          acc.followableEntitiesByTypeOrderedList[entity.entityType] as string[]
        ).push(entity.entityId)
      }

      if (entity.sportradarId) {
        acc.entityToSportradarIdMap[entity.entityId] = entity.sportradarId
        acc.sportradarIdToEntityMap[entity.sportradarId] = entity.entityId
      }

      return acc
    },
    {
      entitiesRegistry: {},
      entitiesOrderedList: [],
      followableEntitiesOrderedList: [],
      entitiesByTypeOrderedList: {},
      followableEntitiesByTypeOrderedList: {},
      entityToSportradarIdMap: {},
      sportradarIdToEntityMap: {},
    } as FormattedSportEntitiesData
  )
}

const fetchSportEntities: FetchSportEntities = async ({ isServerRequest }) => {
  const sportEntitiesRequestURL = new URL(sportEntitiesBaseUrl)
  const sportEntitiesRequestHeaders: Partial<Record<'User-Agent', 'next'>> = {}

  sportEntitiesRequestURL.searchParams.set('size', '50')

  if (isServerRequest) {
    sportEntitiesRequestHeaders['User-Agent'] = 'next'
  }

  const initialResult = await (
    await fetch(sportEntitiesRequestURL, {
      headers: sportEntitiesRequestHeaders,
    })
  ).json()

  if (!Array.isArray(initialResult?.content)) {
    return
  }

  if (initialResult.total_pages < 2) {
    return formatSportEntities(initialResult.content)
  }

  const promiseArray = []

  for (let i = 1; i < initialResult.total_pages; i++) {
    const nextPageUrl = new URL(sportEntitiesBaseUrl)
    nextPageUrl.searchParams.set('page', String(i))
    promiseArray.push(
      fetch(nextPageUrl, {
        headers: sportEntitiesRequestHeaders,
      }).then((res) => res.json())
    )
  }

  const restOfPages = await Promise.all(promiseArray)

  const combinedContent = restOfPages.reduce(
    (acc, page) => {
      if (page?.content && Array.isArray(page.content)) {
        acc.push(...page.content)
      }

      return acc
    },
    [...initialResult.content]
  )

  return formatSportEntities(combinedContent)
}

const isSameSportEntity = (
  sportEntities: FormattedSportEntitiesData | undefined,
  entityId: string,
  sportradarId: string
): boolean =>
  !sportEntities
    ? false
    : sportEntities.entityToSportradarIdMap[entityId] === sportradarId

const getSportEntityById = (
  sportEntities: FormattedSportEntitiesData | undefined,
  isFollowingEnabled: boolean,
  entityId: string
): SportEntity | undefined => {
  const sportEntity = sportEntities?.entitiesRegistry[entityId]

  if (!sportEntity) {
    return
  }

  return {
    ...sportEntity,
    ...(isFollowingEnabled ? {} : { isFollowingEnabled: false }),
  }
}

const getSportEntityBySportradarId = (
  sportEntities: FormattedSportEntitiesData | undefined,
  isFollowingEnabled: boolean,
  sportradarId: string
): SportEntity | undefined => {
  const sportEntity =
    sportEntities?.entitiesRegistry[
      sportEntities?.sportradarIdToEntityMap[sportradarId]
    ]

  if (!sportEntity) {
    return
  }

  return {
    ...sportEntity,
    ...(isFollowingEnabled ? {} : { isFollowingEnabled: false }),
  }
}

const getSportEntitiesList = (
  sportEntities: FormattedSportEntitiesData | undefined,
  isFollowingEnabled: boolean,
  options?: { onlyEntityType?: EntityType; onlyIsFollowable?: boolean }
): SportEntity[] => {
  const { onlyEntityType, onlyIsFollowable } = options ?? {
    onlyIsFollowable: false,
  }

  if (!sportEntities) {
    return []
  }

  if (onlyIsFollowable && !isFollowingEnabled) {
    return []
  }

  const prefilteredResult =
    sportEntities[
      `${onlyIsFollowable ? 'followableE' : 'e'}ntities${onlyEntityType ? 'ByType' : ''}OrderedList`
    ]

  let finalEntityIdList: string[] = []

  if (onlyEntityType && !Array.isArray(prefilteredResult)) {
    finalEntityIdList = prefilteredResult[onlyEntityType] ?? []
  } else if (Array.isArray(prefilteredResult)) {
    finalEntityIdList = prefilteredResult
  }

  return (
    finalEntityIdList.map((entityId) => {
      const sportEntity = sportEntities?.entitiesRegistry[entityId]

      return {
        ...sportEntity,
        ...(isFollowingEnabled ? {} : { isFollowingEnabled: false }),
      }
    }) ?? []
  )
}

export {
  formatSportEntities,
  fetchSportEntities,
  isSameSportEntity,
  getSportEntityById,
  getSportEntityBySportradarId,
  getSportEntitiesList,
}
